import { PAYMENT_VALIDATION_SERVICE_BASE_URL } from "../env";
import { paymentApplicationIdRegex } from "../constants/validations";

export interface CreditCardPaymentRequest {
    paymentRefId: string;
    nameOnCard: string;
    cardNumber: string;
    csv: string;
    expiry: string;
}

export interface BankTransferPaymentRequest {
    paymentRefId: string;
    nameOnAccount: string;
    accountType: "checking" | "savings";
    accountNumber: string;
    routingNumber: string;
}

export interface PaymentTokenResponse {
    paymentToken: string;
}

/**
 * Temporary handling for backwards compatibility with ReMark direct term
 * online submissions. Future implementations should use refId exclusively
 * and at a later point applicationId will be removed as an option in
 * the payment-validation-service.
 */
export function getApplicationOrRefId(paymentRefId: string): { applicationId: string } | { refId: string } {
    return paymentApplicationIdRegex.test(paymentRefId) ? { applicationId: paymentRefId } : { refId: paymentRefId };
}

/**
 * Parses expiry date from MM/YY format to separate month and year values
 */
function parseExpiryDate(expiry: string): { month: number; year: number } {
    const [monthStr, yearStr] = expiry.split("/");
    const month = parseInt(monthStr, 10);
    const year = parseInt(`20${yearStr}`, 10);
    return { month, year };
}

/**
 * Submits credit card payment information for tokenization
 */
export async function submitCreditCardPaymentValidationRequest(
    request: CreditCardPaymentRequest
): Promise<PaymentTokenResponse> {
    const { month: expirationMonth, year: expirationYear } = parseExpiryDate(request.expiry);
    const applicationOrRefId = getApplicationOrRefId(request.paymentRefId);

    const data = JSON.stringify({
        creditCardAccountNumber: request.cardNumber,
        ccExpiryDate: expirationMonth.toString().padStart(2, "0") + expirationYear.toString().slice(2, 4),
        cardHolder: request.nameOnCard,
        ...applicationOrRefId,
    });

    const response = await fetch(`${PAYMENT_VALIDATION_SERVICE_BASE_URL}/tokenizeCCPayment`, {
        method: "POST",
        body: data,
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        if (response.status === 422) {
            const errorData = await response.json();
            const errorMessage = errorData?.statusMessage || errorData?.message || "Payment validation failed";
            throw new Error(errorMessage);
        }
        throw new Error(`Payment validation failed: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Submits EFT payment information for tokenization
 */
export async function submitEftPaymentValidationRequest(request: BankTransferPaymentRequest): Promise<PaymentTokenResponse> {
    const applicationOrRefId = getApplicationOrRefId(request.paymentRefId);

    const data = JSON.stringify({
        routingNumber: request.routingNumber,
        accountNumber: request.accountNumber,
        accountType: request.accountType,
        ...applicationOrRefId,
    });

    const response = await fetch(`${PAYMENT_VALIDATION_SERVICE_BASE_URL}/tokenizeEFTPayment`, {
        method: "POST",
        body: data,
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        if (response.status === 422) {
            const errorData = await response.json();
            const errorMessage = errorData?.statusMessage || errorData?.message || "Payment validation failed";
            throw new Error(errorMessage);
        }
        throw new Error(`Payment validation failed: ${response.statusText}`);
    }

    return response.json();
}
